// Editor sdílení (ACL jádra) pro libovolnou entitu: seznam komu + úroveň + platnost, přidání, zrušení.
// Používají ho Vlastnosti souboru i aplikace (okno desktop/sdileni) – jeden vzhled, jedno API `sharing.*`.
import { h, clear } from '../dom';
import { t, tk, fmtDate } from '../i18n';
import { icon } from '../icons';
import { core, ApiError } from '../api';
import { events } from '../events';
import { toast } from './toast';

export interface Share { id: string; subjectType: 'user' | 'group' | 'all'; subjectId: string | null; subjectName: string; level: 'read' | 'write' | 'manage'; expiresAt: string | null }
export interface SharesPayload { canManage: boolean; shares: Share[]; subjects: { users: { id: string; name: string }[]; groups: { id: string; name: string }[] }; inherits?: boolean }

const LEVELS: ('read' | 'write' | 'manage')[] = ['read', 'write', 'manage'];

export function shareEditor(entityType: string, entityId: string, options: { inheritsHint?: boolean } = {}): HTMLElement {
  const box = h('div', { class: 'share-box' });

  async function load(): Promise<void> {
    clear(box);
    box.appendChild(h('div', { class: 'spinner' }));
    try {
      render(await core<SharesPayload>('sharing', 'list', { type: entityType, id: entityId }));
    } catch (e) {
      box.replaceChildren(h('p', { class: 'muted' }, tk((e as ApiError).message, (e as ApiError).params ?? undefined)));
    }
  }

  function levelSelect(value: string, onChange: (v: string) => void): HTMLSelectElement {
    const sel = h('select', { class: 'input sm' }, LEVELS.map((l) => h('option', { value: l, selected: l === value }, t('core.share.' + l))));
    sel.addEventListener('change', () => onChange(sel.value));
    return sel;
  }

  async function setShare(subjectType: string, subjectId: string | null, level: string, expiresAt: string | null): Promise<void> {
    try {
      await core('sharing', 'set', { type: entityType, id: entityId, subjectType, subjectId, level, expiresAt });
      events.kick();
      toast(t('core.share.added'), 'ok');
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  function render(r: SharesPayload): void {
    clear(box);
    if (!r.canManage) {
      box.appendChild(h('p', { class: 'muted' }, t('core.share.cannot')));
      return;
    }
    if (options.inheritsHint) box.appendChild(h('p', { class: 'muted small' }, t('core.share.inherited')));
    if (!r.shares.length) box.appendChild(h('p', { class: 'muted' }, t('core.share.none')));
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    for (const s of r.shares) {
      const expired = s.expiresAt !== null && s.expiresAt < now;
      const remove = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.share.remove') }, icon('x'));
      remove.addEventListener('click', async () => {
        try {
          await core('sharing', 'remove', { type: entityType, id: entityId, shareId: s.id });
          events.kick();
          toast(t('core.share.removed'), 'ok');
          void load();
        } catch (e) {
          toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
        }
      });
      box.appendChild(h(
        'div',
        { class: 'share-row' + (expired ? ' expired' : '') },
        icon(s.subjectType === 'all' ? 'building' : s.subjectType === 'group' ? 'users' : 'user'),
        h('span', { class: 'share-name' }, s.subjectName, s.expiresAt ? h('span', { class: 'muted small' }, ` · ${t('core.share.expires').toLowerCase()} ${fmtDate(s.expiresAt)}${expired ? ' (' + t('core.share.expired') + ')' : ''}`) : null),
        levelSelect(s.level, (v) => void setShare(s.subjectType, s.subjectId, v, s.expiresAt ? s.expiresAt.slice(0, 10) : null)),
        remove,
      ));
    }
    const typeSel = h('select', { class: 'input sm' }, h('option', { value: 'user' }, t('core.share.user')), h('option', { value: 'group' }, t('core.share.group')), h('option', { value: 'all' }, t('core.share.all')));
    const subjSel = h('select', { class: 'input sm subj' });
    const fill = () => {
      clear(subjSel);
      const list = typeSel.value === 'user' ? r.subjects.users : typeSel.value === 'group' ? r.subjects.groups : [];
      subjSel.hidden = typeSel.value === 'all';
      list.forEach((u) => subjSel.appendChild(h('option', { value: u.id }, u.name)));
    };
    typeSel.addEventListener('change', fill);
    fill();
    const lvl = levelSelect('read', () => undefined);
    const until = h('input', { class: 'input sm', type: 'date', title: t('core.share.expires') });
    const add = h('button', { type: 'button', class: 'btn primary sm' }, t('core.share.add'));
    add.addEventListener('click', () => {
      const subjectType = typeSel.value;
      const subjectId = subjectType === 'all' ? null : subjSel.value;
      if (subjectType !== 'all' && !subjectId) return;
      void setShare(subjectType, subjectId, lvl.value, until.value || null);
    });
    box.appendChild(h('div', { class: 'share-add' }, h('span', { class: 'field-label' }, t('core.share.with')), h('div', { class: 'row' }, typeSel, subjSel, lvl, until, add)));
  }

  void load();
  return box;
}
